import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, ClipboardList, FileText, Star, List } from 'lucide-react';
import {
  useAllCourseSurveys,
  useCreateSurvey,
  useUpdateSurvey,
  useDeleteSurvey,
  useAddSurveyQuestion,
  useDeleteSurveyQuestion,
} from '@/hooks/useCourseSurveys';
import { useModules } from '@/hooks/useCourses';

interface SurveyManagerProps {
  courseId: string;
}

export function SurveyManager({ courseId }: SurveyManagerProps) {
  const { data: surveys, isLoading } = useAllCourseSurveys(courseId);
  const { data: modules } = useModules(courseId);
  const createSurvey = useCreateSurvey();
  const updateSurvey = useUpdateSurvey();
  const deleteSurvey = useDeleteSurvey();
  const addQuestion = useAddSurveyQuestion();
  const deleteQuestion = useDeleteSurveyQuestion();

  const [newSurveyOpen, setNewSurveyOpen] = useState(false);
  const [newSurvey, setNewSurvey] = useState({
    title: '',
    description: '',
    triggerType: 'milestone' as 'milestone' | 'module',
    milestone: '50',
    module_id: '',
  });

  const [newQuestion, setNewQuestion] = useState<{
    surveyId: string | null;
    text: string;
    type: 'text' | 'rating' | 'multiple_choice';
    options: string;
  }>({
    surveyId: null,
    text: '',
    type: 'text',
    options: '',
  });

  const handleCreateSurvey = () => {
    createSurvey.mutate(
      {
        course_id: courseId,
        title: newSurvey.title,
        description: newSurvey.description || undefined,
        milestone: newSurvey.triggerType === 'milestone' ? parseInt(newSurvey.milestone) : null,
        module_id: newSurvey.triggerType === 'module' ? newSurvey.module_id : null,
      },
      {
        onSuccess: () => {
          setNewSurveyOpen(false);
          setNewSurvey({ title: '', description: '', triggerType: 'milestone', milestone: '50', module_id: '' });
        },
      }
    );
  };

  const handleAddQuestion = (surveyId: string) => {
    if (!newQuestion.text.trim()) return;

    addQuestion.mutate(
      {
        survey_id: surveyId,
        question_text: newQuestion.text,
        question_type: newQuestion.type,
        options: newQuestion.type === 'multiple_choice' 
          ? newQuestion.options.split(',').map(o => o.trim()).filter(Boolean) 
          : undefined,
      },
      {
        onSuccess: () => {
          setNewQuestion({ surveyId: null, text: '', type: 'text', options: '' });
        },
      }
    );
  };

  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'text': return <FileText className="h-4 w-4" />;
      case 'rating': return <Star className="h-4 w-4" />;
      case 'multiple_choice': return <List className="h-4 w-4" />;
      default: return null;
    }
  };

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading surveys...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <ClipboardList className="h-5 w-5" />
          Course Surveys
        </h3>
        <Dialog open={newSurveyOpen} onOpenChange={setNewSurveyOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              New Survey
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Survey</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={newSurvey.title}
                  onChange={(e) => setNewSurvey(s => ({ ...s, title: e.target.value }))}
                  placeholder="Survey title..."
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={newSurvey.description}
                  onChange={(e) => setNewSurvey(s => ({ ...s, description: e.target.value }))}
                  placeholder="Brief description..."
                />
              </div>
              <div>
                <Label>Trigger Type</Label>
                <Select
                  value={newSurvey.triggerType}
                  onValueChange={(v: 'milestone' | 'module') => setNewSurvey(s => ({ ...s, triggerType: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="milestone">Progress Milestone</SelectItem>
                    <SelectItem value="module">After Module</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newSurvey.triggerType === 'milestone' && (
                <div>
                  <Label>Milestone</Label>
                  <Select
                    value={newSurvey.milestone}
                    onValueChange={(v) => setNewSurvey(s => ({ ...s, milestone: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50% Complete</SelectItem>
                      <SelectItem value="75">75% Complete</SelectItem>
                      <SelectItem value="100">100% Complete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {newSurvey.triggerType === 'module' && modules && (
                <div>
                  <Label>After Module</Label>
                  <Select
                    value={newSurvey.module_id}
                    onValueChange={(v) => setNewSurvey(s => ({ ...s, module_id: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select module..." />
                    </SelectTrigger>
                    <SelectContent>
                      {modules.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.sequence_order}. {m.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button
                onClick={handleCreateSurvey}
                disabled={!newSurvey.title.trim() || createSurvey.isPending}
                className="w-full"
              >
                {createSurvey.isPending ? 'Creating...' : 'Create Survey'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {!surveys || surveys.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No surveys created yet. Create your first survey to collect student feedback.
          </CardContent>
        </Card>
      ) : (
        <Accordion type="single" collapsible className="space-y-2">
          {surveys.map((survey: any) => (
            <AccordionItem key={survey.id} value={survey.id} className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3 text-left">
                  <span className="font-medium">{survey.title}</span>
                  {survey.milestone && (
                    <Badge variant="secondary">{survey.milestone}% Milestone</Badge>
                  )}
                  {survey.module && (
                    <Badge variant="outline">After: {survey.module.title}</Badge>
                  )}
                  {!survey.is_active && (
                    <Badge variant="destructive">Inactive</Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4 space-y-4">
                {survey.description && (
                  <p className="text-sm text-muted-foreground">{survey.description}</p>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={survey.is_active}
                      onCheckedChange={(checked) => updateSurvey.mutate({ id: survey.id, is_active: checked })}
                    />
                    <Label className="text-sm">Active</Label>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteSurvey.mutate(survey.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium mb-3">Questions ({survey.questions?.length || 0})</h4>
                  
                  {survey.questions?.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {survey.questions
                        .sort((a: any, b: any) => a.sequence_order - b.sequence_order)
                        .map((q: any, idx: number) => (
                          <div key={q.id} className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-start gap-2">
                              {getQuestionTypeIcon(q.question_type)}
                              <div>
                                <p className="text-sm">{idx + 1}. {q.question_text}</p>
                                {q.question_type === 'multiple_choice' && q.options && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Options: {(q.options as string[]).join(', ')}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteQuestion.mutate(q.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Add Question Form */}
                  <div className="space-y-3 p-3 border rounded-lg">
                    <div className="grid grid-cols-[1fr_auto] gap-2">
                      <Input
                        placeholder="Question text..."
                        value={newQuestion.surveyId === survey.id ? newQuestion.text : ''}
                        onChange={(e) => setNewQuestion({ ...newQuestion, surveyId: survey.id, text: e.target.value })}
                      />
                      <Select
                        value={newQuestion.surveyId === survey.id ? newQuestion.type : 'text'}
                        onValueChange={(v: 'text' | 'rating' | 'multiple_choice') =>
                          setNewQuestion({ ...newQuestion, surveyId: survey.id, type: v })
                        }
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="rating">Rating (1-5)</SelectItem>
                          <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {newQuestion.surveyId === survey.id && newQuestion.type === 'multiple_choice' && (
                      <Input
                        placeholder="Options (comma-separated): Option A, Option B, Option C"
                        value={newQuestion.options}
                        onChange={(e) => setNewQuestion({ ...newQuestion, options: e.target.value })}
                      />
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleAddQuestion(survey.id)}
                      disabled={newQuestion.surveyId !== survey.id || !newQuestion.text.trim()}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Question
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
